import { Injectable } from '@angular/core';
import { OrigamiResponse, ServiceBase } from './base/service.base';
import { Observable, tap } from 'rxjs';
import { TopicField, TopicUtils } from '../utils/topic-utils';
import { QueryType } from '@app/origami-api/constants/query-type';
import { ViewFilter } from '@app/shared/layout/view-mode/view-mode';
import { Converter } from '@app/shared/utils/converter';
import { HttpXhrBackend } from '@angular/common/http';
import { CASAuthRequestProxy } from './base/request-proxy/cas-auth.request-proxy.class';
import { TimeZoneService } from '@app/services/time-zone.service';
import { SessionService } from '@app/session/session.service';

export enum QueryViewsSubFiltersId {
  QUERY_SHOW_MY_QUERIES_ONLY = 'query_show_my_queries_only',
  QUERY_QUERIES_FILTER_WORKFLOW = 'query_queries_filter_workflow',
  QUERY_QUERIES_FILTER_AUDIT = 'query_queries_filter_audit',
  QUERY_QUERIES_FILTER_GENERAL = 'query_queries_filter_general',
  QUERY_QUERIES_FILTER_SSU = 'query_queries_filter_ssu',
}

export enum QueryTopicType {
  DocumentQuery = 1,
  RecordQuery = 2,
}

export namespace QueryServiceModel {
  export interface QueryView {
    EntitiesCount: number;
    ViewFilterDisplayName: string;
    ViewFilterId: string;
    ViewMode: number;
    ViewFilterIdAdditional?: string;
  }
  //here because it's temporary anyway
  export enum QueryFieldNames {
    DocumentId = '$$DocumentId$$',
    DocumentTitle = '$$DocumentTitle$$',
    DocumentName = '$$DocumentName$$',
    DocumentType = '$$DocumentType$$',
    DocumentFileName = '$$DocumentFileName$$',
    QueryId = '$$QueryId$$',
    QueryDate = '$$QueryDate$$',
    QueryCreatedBy = '$$QueryCreatedBy$$',
    QueryCreatedByRole = '$$QueryCreatedByRole$$',
    QueryType = '$$QueryType$$',
    QueryStatus = '$$QueryStatus$$',
    QueryRecipients = '$$QueryRecipients$$',
    QueryRecipientsRoles = '$$QueryRecipientsRoles$$',
    QueryRecipientsEmails = '$$QueryRecipientsEmails$$',
    QuerySubject = '$$QuerySubject$$',
    QueryRequest = '$$QueryRequest$$',
    QueryResponse = '$$QueryResponse$$',
    QueryResponseDate = '$$QueryResponseDate$$',
    QueryIssues = '$$QueryIssues$$',
    DocumentCategory = '$$DocumentCategory$$',
    DocumentIndex = '$$DocumentIndex$$',
    TopicTypeId = '$$TopicTypeId$$',
    NotRequiredForCompleteness = '$$NotRequiredForCompleteness$$',
    CompletenessStatusId = '$$CompletenessStatusId$$',
  }

  export enum QueryTicketViewMode {
    MyQueries = 1,
    BySender = 2,
    ByRecipient = 3,
    ByStatus = 4,
    BySite = 5,
    ByCountry = 6,
    ByDocumentType = 7,
    ById = 8,
    ByTeam = 9,
    ByOwner = 10,
  }

  export enum QueryCustomStatus {
    expired = 100, //revise
  }

  export enum CommonQueryStatus {
    Pending = 1,
    InProgress = 2,
    Resolved = 3,
  }

  export enum QueryTypeFilterBit {
    None = 0,
    Workflow = 1,
    Audit = 2,
    General = 4,
    SSU = 8,
  }

  export enum RelatedEntityType {
    Document = 1,
    Placeholder = 2,
    MissingDocument = 3,
  }

  export interface QueryTicketFileData {
    FileId: number;
    FilePath: string;
    OriginalFileName: string;
    QueryTicketActivityId: number;
    QueryTicketId: number;
  }

  export interface Query {
    siteId: number;
    documentId: number;
    parentId: number;
    documentName: string;
    queryId: string;
    queryType: QueryType;
    queryDate: string;
    queryCreatedBy: number;
    codingTypeName: string;
    documentTypeName: string;
    subject?: string;
    status?: CommonQueryStatus;
    documentFileName: string;
    documentTopicTypeId: number;
    queryResponse: string;
  }

  export interface QueryTicketData {
    QueryTicketId: number;
    SiteId: number;
    RelatedEntityTypeId: number;
    RelatedEntityId: number;
    CreatedBy: number;
    CreatedDate: string;
    CreatedDateString?: string;
    LastUpdatedBy: number;
    LastUpdatedDate: string;
    LastUpdatedDateString?: string;
    StatusId: CommonQueryStatus;
    QueryTypeId: QueryType;
    QueryEntityId: number;
    QueryFields: TopicField[];
    QueryRequestFiles?: QueryTicketFileData[];
    Title?: string;
    Comments?: string;
  }

  export interface QueryTicketResult {
    PageNumber?: number;
    Records: QueryTicketData[];
    TotalRowCount: number;
  }
}

export namespace QueryServiceOptions {
  export interface GetQueryViews {
    siteId: number;
    dataRoomId?: number;
    viewMode?: number;
  }

  export interface GetQueryTicketViews {
    siteId: number;
    viewMode: QueryServiceModel.QueryTicketViewMode;
    viewFilter: string;
    myQueriesOnly?: boolean;
    queryTypeFilter?: number;
    userId?: number;
    filtersGetter?: () => ViewFilter[];
    queryTopicType: QueryTopicType;
  }

  export interface GetQueries {
    dataRoomId?: number;
    limit?: number;
    siteId: number;
    start?: number;
    viewFilter: string;
    viewMode: number;
    userId?: string;
    searchQuery?: string;
  }

  export interface GetQueryTicketsByView {
    siteId: number;
    viewMode: QueryServiceModel.QueryTicketViewMode;
    viewFilter: string;
    start?: number;
    limit?: number;
    orderByClause?: string;
    searchClause?: string;
    queryId?: number;
    queryTopicType: QueryTopicType;
    hasData?: boolean;
    queryTypeFilter?: number;
  }
}

@Injectable({
  providedIn: 'root',
})
export class QueryService extends ServiceBase {
  serviceUrl = 'QueryTicketService.svc';

  constructor(
    backend: HttpXhrBackend,
    requestProxy: CASAuthRequestProxy,
    private timeZoneService: TimeZoneService,
    private sessionService: SessionService,
  ) {
    super(backend, requestProxy);
  }

  getQueryViews(
    body: QueryServiceOptions.GetQueryTicketViews,
  ): Observable<OrigamiResponse<QueryServiceModel.QueryView[]>> {
    return this.origamiPost('/GetQueryTicketViews', body, null, null, true);
  }

  getQueries(
    params: QueryServiceOptions.GetQueryTicketsByView,
  ): Observable<OrigamiResponse<QueryServiceModel.QueryTicketResult>> {
    return this.origamiPost('/GetQueryTicketsByView', params, null, null, true).pipe(
      tap((res) => {
        if (!res.Result) {
          return;
        }
        const result: QueryServiceModel.QueryTicketResult = res.Result;
        result.Records.forEach((record) => {
          record.CreatedDateString = this.formatDate(record.CreatedDate);
          record.LastUpdatedDateString = this.formatDate(record.LastUpdatedDate);

          [QueryServiceModel.QueryFieldNames.QueryDate, QueryServiceModel.QueryFieldNames.QueryResponseDate].forEach(
            (fieldName) => {
              this.convertDateField(record.QueryFields, fieldName);
            },
          );
        });
      }),
    );
  }

  private convertDateField(queryFields: TopicField[], fieldName: QueryServiceModel.QueryFieldNames): void {
    const dateField = TopicUtils.getTopicField(queryFields, fieldName);
    if (!dateField?.Fieldvalue) {
      return;
    }
    const date = Converter.DateAndTime.parseStringToJSDate(
      dateField.Fieldvalue,
      this.sessionService.domain.timeZone.IanaZoneName,
    );
    dateField.Fieldvalue = this.timeZoneService.getFormattedDateWithRoomTimeZone(date, true, true);
  }

  private formatDate(dateString: string): string {
    if (!dateString) {
      return null;
    }
    const date = Converter.DateAndTime.fromCSharpString(dateString);
    return this.timeZoneService.getFormattedDateWithRoomTimeZone(date);
  }

  getQuery(params: {
    siteId: number;
    topicId: number;
  }): Observable<OrigamiResponse<QueryServiceModel.QueryTicketData>> {
    return this.origamiPost('/GetQuery', params, null, null, true);
  }

  //TODO:
  createQueries(body) {
    return this.origamiPost('/CreateQuery', body, null, null, true);
  }

  //TODO:
  editQuery(body) {
    return this.origamiPost('/EditQuery', body, null, null, true);
  }
  //TODO:
  deleteQuery(params: {
    siteId: number;
    topics: { TopicId: number; TopicName: string }[];
  }): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/DeleteQuery', params, null, null, true);
  }
}
